import { Controller } from 'stimulus'

/**
 * Manages js for the clear disable inputs functionality (StimulusJS controller)
 *
 * @class ClearDisableInputsController
 * @memberof Controllers
 * @description Triggers disable/re-enable of form inputs. When disabled the input's
 * value is cleared which varies by input type, e.g. radio/checkboxes are unchecked
 * and inputs are cleared. This status change is propagated to all child inputs as
 * well.
 *
 * @example
 * -# Example Markup (HAML)
 *
 *  .container{ data: { controller: 'clear-disable-inputs', 'clear-disable-inputs-css-class': 'input--disabled' } }
 *   %h2 What's your favorite fruit?
 *
 *   = label_tag :fruit_refuse do
 *     = check_box_tag :fruit_refuse, 'true', fruit_refuse, id: 'fruit_refuse', 'data-action': 'clear-disable-inputs#trigger'
 *
 *   .fruits{ data: { target: 'clear-disable-inputs.disableable', 'clear-disable-id': 'fruit_refuse' } }
 *     = label_tag :apple do
 *       = check_box_tag :apple, 'apples'
 *     = label_tag :bananas do
 *       = check_box_tag :banana, 'bananas'*
 */
export default class extends Controller {
  static targets = [
    'disableable',
  ]

  /**
   * @function Controllers.ClearDisableInputsController.connect
   * @memberof Controllers
   * @description Called on page load. Clears and disables form inputs.
   */
  connect() {
    this.cssName = this.data.get('cssClass')

    this.disableableTargets.forEach(disableable => {
      const id = disableable.dataset.clearDisableId
      const target = document.getElementById(id)
      if (target) {
        this.clearDisableInputs(target)
      }
    })
  }

  /**
   * @function Controllers.ClearDisableInputsController.trigger
   * @memberof Controllers
   * @description Calls clearDisableInputs function on a given event target.
   *
   * @param {Event} event
   */
  trigger(event) {
    const target = event.target
    this.clearDisableInputs(target)
  }

  /**
   * @function Controllers.ClearDisableInputsController.clearDisableInputs
   * @memberof Controllers
   * @description Iterates over disableable targets and determines disabling
   * behavior based on input type. Processes all labels and inputs for a given
   * disableable target and enables/disables input and adds/removes disabled
   * class based on disabling value.
   *
   * @param {Element} target
   */
  clearDisableInputs(target) {
    const disabling = this.shouldDisableBasedOn(target)
    this.disableableTargets.forEach(disableable => {
      if (disableable.dataset.clearDisableId === target.id) {
        const labels = Array.from(disableable.getElementsByTagName('label'))
        labels.forEach(label => {
          if (disabling) {
            label.classList.add(this.cssName)
          } else {
            label.classList.remove(this.cssName)
          }

          const inputs = Array.from(label.getElementsByTagName('input'))
          inputs.forEach(input => {
            if (disabling) {
              this.disableElement(input)
            } else {
              this.enableElement(input)
            }
          })
        })
      }
    })
  }

  /**
   * @function Controllers.ClearDisableInputsController.disableElement
   * @memberof Controllers
   * @description Uncheck/clear and disable targeted element.
   *
   * @param {Element} elem
   */
  disableElement(elem) {
    if ([ 'checkbox', 'radio' ].includes(elem.type)) {
      elem.checked = false
    } else {
      elem.value = ''
    }

    elem.dispatchEvent(new CustomEvent('change'))
    elem.disabled = true
  }

  /**
   * @function Controllers.ClearDisableInputsController.enableElement
   * @memberof Controllers
   * @description Enable targeted element.
   *
   * @param {Element} elem
   */
  enableElement(elem) {
    if ([ 'checkbox', 'text' ].includes(elem.type)) {
     elem.disabled = false
    }
  }

  /**
   * @function Controllers.ClearDisableInputsController.shouldDisableBasedOn
   * @memberof Controllers
   * @description Determines disabling state based on input type. For radio
   * buttons returns true if button is checked. For text boxes returns true
   * if value exists. Skips checkboxes.
   *
   * @param {Input} input
   * @return {Boolean} The result of input type switch statement.
   */
  shouldDisableBasedOn(input) {
    let result = false

    switch (input.type) {
      case 'checkbox':
      case 'radio':
        if (input.checked) {
          result = true
        }
        break
      case 'text':
        if (input.value !== '') {
          result = true
        }
        break
    }
    return result
  }
}
